/* eslint-disable */

import React, {useEffect, useState, useMemo} from 'react'
import PageContainer  from '../../components/PageContainer/PageContainer';
import { selectShouldOpen, selectMessage, setOpen, selectSeverity, startIngestion, setPage, selectLocations, selectPageSize, fetchLocations, selectRowCount, setPageSize, selectPage } from './slice';
import { generateTagsAndDescriptions } from '../Restaurant/slice';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { Container, ContentBody, ContentHeader, Title, ActionToolbar, LocationsGrid, LocationCard, LocationContent, LocationInfo, SearchContainer, PaginationContainer, PaginationInfo, PaginationControls, PageSizeControl } from './Location.styled';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Location as LocationType } from './types';


const Location: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const locationsServer = useAppSelector(selectLocations);
  const [locations, setLocations] = useState(locationsServer);
  const currPage = useAppSelector(selectPage);
  const pageSize = useAppSelector(selectPageSize);
  const rowCount = useAppSelector(selectRowCount);
  const [selectedLocation, setSelectedLocation] = useState<LocationType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    dispatch(fetchLocations(0));
  }, [dispatch])

  useEffect(()=>{
    setLocations(locationsServer);
  },[locationsServer]);

  const openServer = useAppSelector(selectShouldOpen);
  const setOpenServer = (open: boolean) => {
    dispatch(setOpen(open));
  }
  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) {
      return locations;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return locations?.filter(location =>
      (location.city?.toLowerCase().includes(lowerQuery) ?? false) ||
      (location.state?.toLowerCase().includes(lowerQuery) ?? false) ||
      (location.zipCode?.toLowerCase().includes(lowerQuery) ?? false) ||
      (location.country?.toLowerCase().includes(lowerQuery) ?? false) ||
      (location.query?.toLowerCase().includes(lowerQuery) ?? false)
    ) ?? [];
  }, [locations, searchQuery]);

  const totalPages = Math.ceil(rowCount / pageSize);
  const startIndex = currPage * pageSize + 1;
  const endIndex = Math.min((currPage + 1) * pageSize, rowCount);
  const hasNextPage = currPage < totalPages - 1;
  const hasPreviousPage = currPage > 0;

  const handleLocationClick = (location: LocationType) => {
    setSelectedLocation(location);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLocation(null);
  };

  const handlePageSizeChange = (event: any) => {
    dispatch(setPageSize(event.target.value));
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      dispatch(setPage(currPage + 1));
      dispatch(fetchLocations(currPage + 1));
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      dispatch(setPage(currPage - 1));
      dispatch(fetchLocations(currPage - 1));
    }
  };

  return (
    <PageContainer title='Locations' open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
      <Container>
        <ContentHeader>
          <Title>Locations</Title>
          <ActionToolbar>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/locations/add-location')}
            >
              Add Location
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                dispatch(startIngestion());
              }}
            >
              Start Ingestion
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                dispatch(generateTagsAndDescriptions());
              }}
            >
              Generate Tags & Descriptions
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                dispatch(fetchLocations(currPage));
              }}
            >
              Refresh
            </Button>
          </ActionToolbar>
        </ContentHeader>

        <SearchContainer>
          <TextField
            label="Search locations"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by city, state, zip code, country, or query"
            fullWidth
            sx={{ maxWidth: '400px' }}
          />
        </SearchContainer>

        <ContentBody>
          <LocationsGrid>
            {filteredLocations.map((location) => (
              <LocationCard key={location.locationID} onClick={() => handleLocationClick(location)}>
                <LocationContent>
                  <LocationInfo>
                    <div><strong>City:</strong> {location.city}</div>
                    <div><strong>State:</strong> {location.state}</div>
                    <div><strong>Zip Code:</strong> {location.zipCode}</div>
                    <div><strong>Country:</strong> {location.country}</div>
                    <div><strong>Query:</strong> {location.query === '' || location.query === null ? location.zipCode : location.query}</div>
                    <div><strong>Processed:</strong> {location.count}</div>
                    <div><strong>Next Date:</strong> {location.nextProcessedDate}</div>
                  </LocationInfo>
                </LocationContent>
              </LocationCard>
            ))}
          </LocationsGrid>
        </ContentBody>

        {rowCount > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {rowCount > 0 ? startIndex : 0} to {endIndex} of {rowCount} locations
            </PaginationInfo>

            <PaginationControls>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasPreviousPage}
                onClick={handlePreviousPage}
              >
                Previous
              </Button>
              <PaginationInfo>
                Page {currPage + 1} of {totalPages}
              </PaginationInfo>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasNextPage}
                onClick={handleNextPage}
              >
                Next
              </Button>
            </PaginationControls>

            <PageSizeControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Items per page"
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </PageSizeControl>
          </PaginationContainer>
        )}

        {/* Location Detail Modal */}
        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedLocation?.city}, {selectedLocation?.state}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a1e3d' }}>
                  City
                </Typography>
                <Typography variant="body2">
                  {selectedLocation?.city}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a1e3d' }}>
                  State
                </Typography>
                <Typography variant="body2">
                  {selectedLocation?.state}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a1e3d' }}>
                  Zip Code
                </Typography>
                <Typography variant="body2">
                  {selectedLocation?.zipCode}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a1e3d' }}>
                  Country
                </Typography>
                <Typography variant="body2">
                  {selectedLocation?.country}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a1e3d' }}>
                  Query
                </Typography>
                <Typography variant="body2">
                  {selectedLocation?.query === '' || selectedLocation?.query === null ? selectedLocation?.zipCode : selectedLocation?.query}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a1e3d' }}>
                  Processed
                </Typography>
                <Typography variant="body2">
                  {selectedLocation?.count}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a1e3d' }}>
                  Next Processed Date
                </Typography>
                <Typography variant="body2">
                  {selectedLocation?.nextProcessedDate}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageContainer>
  )
}

export default withAuthenticationRequired(Location)
